import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Loader2, Check, X, Pencil, AlertTriangle, ShieldCheck, BarChart3, ListChecks, Send, Link2 } from "lucide-react";
import RebookAnalytics from "./RebookAnalytics";
import ExtensionApprovals from "./ExtensionApprovals";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const inp = "px-3 py-2 rounded-md bg-[#0A0A0A] border border-white/15 text-white text-sm outline-none focus:border-[#F2B705]";

const Flag = ({ bad, okLabel, badLabel }) => (
  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${bad ? "bg-red-500/15 text-red-400 border-red-500/40" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"}`}>
    {bad ? badLabel : okLabel}
  </span>
);

const SendLinkPanel = ({ token }) => {
  const [bookings, setBookings] = useState([]);
  const [sel, setSel] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setBookings((r.data || []).filter((b) => ["active", "confirmed"].includes(b.lifecycle_status) && b.status !== "cancelled")))
      .catch(() => {});
  }, [token]);
  const act = async (sms) => {
    if (!sel) { toast.error("Choose a renter first."); return; }
    setBusy(sms ? "sms" : "copy");
    try {
      const { data } = await axios.post(`${API}/rebooking/send-link/${sel}?send_sms=${sms}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      if (sms && data.sent_sms) {
        toast.success("Secure self-service links texted to the renter.");
      } else {
        if (sms && !data.sent_sms) toast.error("No phone on file — here's the link instead.");
        try {
          await navigator.clipboard.writeText(data.rebook_url);
          if (!sms) toast.success("Rebooking link copied — paste it anywhere.");
        } catch {
          toast.info(`Link ready — copy it manually: ${data.rebook_url}`, { duration: 12000 });
        }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the link."); }
    finally { setBusy(""); }
  };
  return (
    <div className="bg-[#0C0C0C] border border-[#F2B705]/25 rounded-lg p-4" data-testid="rebooking-send-link-panel">
      <div className="font-display font-bold text-sm flex items-center gap-2"><Send size={14} className="text-[#F2B705]" /> Send a Self-Service Link</div>
      <p className="text-white/45 text-xs mt-1">Text a renter their secure links to extend, rebook or schedule their return — no phone calls needed. Requests come back here for your approval.</p>
      <div className="flex flex-wrap gap-2 mt-3">
        <select data-testid="send-link-booking" className={`${inp} flex-1 min-w-[220px] appearance-none`} value={sel} onChange={(e) => setSel(e.target.value)}>
          <option value="" className="bg-black">Choose a renter…</option>
          {bookings.map((b) => (
            <option key={b.id} value={b.id} className="bg-black">
              {b.name} — {b.vehicle} (due {(b.return_date || "").slice(0, 10)}){b.phone ? "" : " — no phone"}
            </option>
          ))}
        </select>
        <button data-testid="send-link-sms" disabled={!!busy} onClick={() => act(true)}
          className="bg-[#F2B705] text-black text-xs font-bold px-4 py-2 rounded-md hover:bg-white flex items-center gap-1.5 disabled:opacity-60">
          {busy === "sms" ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />} Text Links to Renter
        </button>
        <button data-testid="send-link-copy" disabled={!!busy} onClick={() => act(false)}
          className="border border-white/20 text-white/70 text-xs font-bold px-4 py-2 rounded-md hover:bg-white/5 flex items-center gap-1.5 disabled:opacity-60">
          {busy === "copy" ? <Loader2 className="animate-spin" size={13} /> : <Link2 size={13} />} Copy Link
        </button>
      </div>
    </div>
  );
};

export default function RebookingRequests({ token, onAuthError }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState("");
  const [modId, setModId] = useState("");
  const [mod, setMod] = useState({});
  const [view, setView] = useState("requests");

  const load = useCallback(() => {
    axios.get(`${API}/rebooking/requests`, auth).then((r) => setList(r.data))
      .catch((e) => { if (e?.response?.status === 401) onAuthError?.(); });
    // eslint-disable-next-line
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const act = async (r, action, body = {}) => {
    setBusy(r.id + action);
    try {
      const { data } = await axios.post(`${API}/rebooking/${r.id}/${action}`, body, auth);
      toast.success(action === "approve" ? `Approved — booking created, ${r.payment_method === "Card (Stripe)" ? "payment link + " : ""}agreement sent.`
        : action === "decline" ? "Declined — professional message sent."
        : `Offer sent: ${data.offer_url}`, { duration: 8000 });
      setModId(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); }
    finally { setBusy(""); }
  };

  if (!list) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  const pending = list.filter((r) => ["Pending Approval", "Offer Sent"].includes(r.status));
  const past = list.filter((r) => !["Pending Approval", "Offer Sent"].includes(r.status));

  const Row = (r) => {
    const e = r.eligibility;
    return (
      <div key={r.id} data-testid={`rebooking-row-${r.id}`} className="bg-[#0C0C0C] border border-white/10 rounded-lg p-4 space-y-2.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <span className="font-display font-bold text-lg">{r.name}</span>
            <span className="ml-3 text-[#F2B705] font-semibold">{r.requested_vehicle}</span>
            <span className={`ml-3 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border ${r.status === "Pending Approval" ? "bg-amber-400/10 text-amber-300 border-amber-400/40" : r.status === "Approved" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "bg-white/5 text-white/50 border-white/15"}`}>{r.status}</span>
          </div>
          <span className="text-white/40 text-xs">{fmtDateTime(r.created_at)}</span>
        </div>
        <div className="text-sm text-white/60 flex flex-wrap gap-x-5 gap-y-1">
          <span>{r.pickup_date} → {r.return_date} ({r.quoted_days}d)</span>
          <span className="text-white font-semibold">{money(r.quoted_total)}{r.premium ? " (Premium pkg)" : ""}</span>
          <span>Pay: {r.payment_method}</span>
          {r.source_channel && <span className="text-[#F2B705]/80">Via: {r.source_channel}</span>}
          {r.current_vehicle && <span>Currently in: {r.current_vehicle}</span>}
          <span>{r.phone} · {r.email}</span>
        </div>
        {r.notes && <div className="text-white/40 text-xs">Notes: {r.notes}</div>}
        {e && (
          <div className="flex flex-wrap gap-1.5" data-testid={`rebooking-eligibility-${r.id}`}>
            <Flag bad={!e.vehicle_available} okLabel="Vehicle available" badLabel="Vehicle conflict" />
            <Flag bad={e.unpaid_balance > 0.5} okLabel="No balance" badLabel={`Owes ${money(e.unpaid_balance)}`} />
            <Flag bad={e.currently_late} okLabel="Not late" badLabel="Currently late" />
            <Flag bad={e.late_incidents > 0} okLabel="No late history" badLabel={`${e.late_incidents} late incident(s)`} />
            <Flag bad={e.open_claims > 0} okLabel="No open claims" badLabel={`${e.open_claims} open claim(s)`} />
            <Flag bad={e.docs_expired} okLabel="Docs current" badLabel="Insurance expired" />
            <Flag bad={e.dnr} okLabel="Not blocked" badLabel={`DO NOT RENT${e.dnr_reason ? ` — ${e.dnr_reason}` : ""}`} />
            <span className={`text-[10px] uppercase font-black px-2 py-0.5 rounded-full flex items-center gap-1 ${e.eligible ? "bg-emerald-500 text-black" : "bg-red-500 text-white"}`}>
              {e.eligible ? <ShieldCheck size={11} /> : <AlertTriangle size={11} />} {e.eligible ? "Eligible" : "Review needed"}
            </span>
          </div>
        )}
        {["Pending Approval", "Offer Sent"].includes(r.status) && (
          <div className="flex flex-wrap gap-2 pt-1">
            <button data-testid={`rebooking-approve-${r.id}`} disabled={!!busy} onClick={() => {
              if (window.confirm(`Approve ${r.name} for the ${r.requested_vehicle} at ${money(r.quoted_total)}? This creates the booking and sends ${r.payment_method === "Card (Stripe)" ? "the Stripe payment link and " : ""}the rental agreement.`)) act(r, "approve");
            }} className="bg-emerald-500 text-black text-xs font-bold px-4 py-2 rounded-md hover:bg-emerald-400 flex items-center gap-1.5">
              {busy === r.id + "approve" ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Approve
            </button>
            <button data-testid={`rebooking-decline-${r.id}`} disabled={!!busy} onClick={() => {
              if (window.confirm(`Decline ${r.name}'s request? A professional message is sent (no internal reasons shared).`)) act(r, "decline");
            }} className="border border-red-400/40 text-red-300 text-xs font-bold px-4 py-2 rounded-md hover:bg-red-400/10 flex items-center gap-1.5">
              <X size={13} /> Decline
            </button>
            <button data-testid={`rebooking-modify-${r.id}`} onClick={() => { setModId(modId === r.id ? "" : r.id); setMod({ requested_vehicle: r.requested_vehicle, pickup_date: r.pickup_date, return_date: r.return_date, final_total: r.quoted_total, notes: "" }); }}
              className="border border-white/15 text-white/70 text-xs font-bold px-4 py-2 rounded-md hover:bg-white/5 flex items-center gap-1.5">
              <Pencil size={13} /> Modify Offer
            </button>
          </div>
        )}
        {modId === r.id && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 border-t border-white/10 pt-3" data-testid={`rebooking-modify-form-${r.id}`}>
            <input className={inp} placeholder="Vehicle" value={mod.requested_vehicle} onChange={(ev) => setMod({ ...mod, requested_vehicle: ev.target.value })} />
            <input className={`${inp} [color-scheme:dark]`} type="date" value={mod.pickup_date} onChange={(ev) => setMod({ ...mod, pickup_date: ev.target.value })} />
            <input className={`${inp} [color-scheme:dark]`} type="date" value={mod.return_date} onChange={(ev) => setMod({ ...mod, return_date: ev.target.value })} />
            <input className={inp} type="number" placeholder="Price $" value={mod.final_total} onChange={(ev) => setMod({ ...mod, final_total: parseFloat(ev.target.value) || 0 })} />
            <button data-testid={`rebooking-send-offer-${r.id}`} disabled={!!busy} onClick={() => act(r, "modify", mod)}
              className="col-span-2 sm:col-span-4 bg-[#F2B705] text-black text-xs font-bold px-4 py-2 rounded-md hover:bg-white">Send Revised Offer to Customer</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="rebooking-manager">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-black text-2xl flex items-center gap-2"><RefreshCw className="text-[#F2B705]" size={22} /> Rebooking Requests</h2>
          <p className="text-white/50 text-sm mt-1">{pending.length} pending. Requests never auto-confirm — you approve, decline or modify each one. Approval creates the booking, sends the payment link (card requests) and the rental agreement automatically.</p>
        </div>
        <div className="flex gap-1.5">
          <button data-testid="rebooking-view-requests" onClick={() => setView("requests")}
            className={`flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-md border transition-colors ${view === "requests" ? "bg-[#F2B705] text-black border-[#F2B705]" : "border-white/15 text-white/60 hover:bg-white/5"}`}>
            <ListChecks size={13} /> Requests
          </button>
          <button data-testid="rebooking-view-analytics" onClick={() => setView("analytics")}
            className={`flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-md border transition-colors ${view === "analytics" ? "bg-[#F2B705] text-black border-[#F2B705]" : "border-white/15 text-white/60 hover:bg-white/5"}`}>
            <BarChart3 size={13} /> Analytics
          </button>
        </div>
      </div>
      {view === "analytics" ? <RebookAnalytics token={token} /> : (
        <>
      <ExtensionApprovals token={token} />
      <SendLinkPanel token={token} />
      <div className="space-y-2.5">
        {pending.length === 0 && <p className="text-white/40 text-sm border border-white/10 rounded-lg bg-[#0A0A0A] p-5 text-center">No pending rebooking requests.</p>}
        {pending.map(Row)}
      </div>
      {past.length > 0 && (
        <div>
          <h3 className="text-white/40 text-xs uppercase tracking-widest mb-2">Decided</h3>
          <div className="space-y-2.5 opacity-70">{past.slice(0, 15).map(Row)}</div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
